import { observable, action, computed, reaction } from 'mobx';
import lunr from 'lunr';
import isFunction from 'lodash/isFunction';
import { api } from '../services';
import { filterStore } from 'air-filter-mobx-store';

class OrdersStore {
  @observable orders = [];
  @observable availableVehicles = [];
  @observable loadedAt;
  @observable err;

  constructor(filterStore) {
    this.filterStore = filterStore;
    reaction(
      () => this.filterStore.globalFilterFunction,
      fn => {
        const orders = this.orders.map(order => {
          if (fn(order)) {
            return { ...order, hidden: false };
          } else {
            return { ...order, hidden: true };
          }
        });
        this.setOrders(orders);
      }
    );
  }

  buildIndex(orders) {
    this.fullTextSearchIndex = lunr(function() {
      this.field('externalOfferId');
      this.field('externalOrderId');
      this.field('description');
      this.ref('id');

      orders.forEach(order => {
        this.add(order);
      });
    });
    return orders;
  }

  @action
  load(page=1, size=10) {
    return api('getOrders', { page, size }).then(results => {
      let orders = results.rows;
      this.setOrders(orders);
      this.setLoadedAt();

      setTimeout(() => {
        this.buildIndex(orders);
      }, 500);
    })
      .catch(err => {
        this.err = err;
      })
  }

  @action setLoadedAt() {
    this.loadedAt = new Date().getTime();
  }

  @action
  setOrders(orders) {
    this.orders = orders;
  }


  @action
  setAvailableVehicles(vehicles) {
    this.availableVehicles = vehicles;
  }

  @computed get filtered() {
    if (this.filterStore.globalFilterFunction) {
      return this.orders.filter(this.filterStore.globalFilterFunction);
    } else {
      return this.orders;
    }
  }

  filterLength = (filterType, attribute) => {
    if (isFunction(this.filterStore.predictionFns[`${filterType}.${attribute}`])) {
      return this[attribute].filter(this.filterStore.predictionFns[`${filterType}.${attribute}`]).length;
    }
    return this[attribute].length;
  };

  fullTextSearchFns = text => {
    let matches = this.fullTextSearchIndex.search(text);
    return {
      search: order => matches.findIndex(match => match.ref === order.id) > -1,
      sort: (order1, order2) => {
        let score1 = matches.find(match => match.ref === order1.id).score;
        let score2 = matches.find(match => match.ref === order2.id).score;
        return score2 - score1;
      }
    };
  };

  @action
  create(carId, externalOfferId, externalOrderId, description, filesList) {
    this.loading = true;
    return api('createOrder', carId, externalOfferId, externalOrderId, description)
      .then((result) => {
        if (filesList.length > 0) {
          let fileProcessed = 0;
          filesList.forEach(file => {
            let fileList = [];
            fileList.push(file);
            api('uploadOrderFile', result.success.id, fileList).then(fileResult => {
              fileProcessed++;
              if(fileProcessed === filesList.length) {
                this.load();
              }
            });	
          });
        } else {
          this.load();
        }
      })
      .catch( err => {
        this.error = err;
      })
      .finally(() => {
        this.loading = false;
      })
  }

  @action
  edit(id, carId, externalOfferId, externalOrderId, description, filesList) {
    this.loading = true;
    return api('editOrder', id, carId, externalOfferId, externalOrderId, description)
      .then(() => {
        if (filesList.length > 0) {
          let fileProcessed = 0;
          filesList.forEach(file => {
            let fileList = [];
            fileList.push(file);
            api('uploadOrderFile', id, fileList).then(fileResult => {
              fileProcessed++;
              if(fileProcessed === filesList.length) {
                this.load();
              }
            });	
          });
        } else {
          this.load();
        }
      })
      .catch( err => {
        this.error = err;
      })
      .finally(() => {
        this.loading = false;
      })
  }

  @action
  accept(id) {
    this.loading = true;
    return api('acceptOrder', id)
      .then(() => {
        this.load();
      })
      .catch( err => {
        this.error = err;
      })
      .finally(() => {
        this.loading = false;
      })
  }

  @action
  decline(id) {
    this.loading = true;
    return api('declineOrder', id)
      .then(() => {
        this.load();
      })
      .catch( err => {
        this.error = err;
      })
      .finally(() => {
        this.loading = false;
      })
  }

  @action
  deleteOrder(order) {
    return api('deleteOrder', order.id).then(results => {
      if (results.success) {
        this.load();
      }
    });
  }

  @action
  loadAvailableVehicles = () => {
    api('getOrderAvailableVehicles')
    .then(vehicles => {
      const availableVehicles = [];

      for (let vehicle of vehicles) {
        availableVehicles.push(
          {
            key: vehicle.id.toString(),
            title: `${vehicle.plate}`,
            description: `${vehicle.make.descrizione} ${vehicle.model.descrizione}`
          }
        )
      }
      this.setAvailableVehicles(availableVehicles);
    })
    .catch( err => {
      this.error = err;
    })
    .finally(() => {
      this.loading = false;
    });
  };
}

export default new OrdersStore(filterStore);
  


// WEBPACK FOOTER //
// ./src/stores/ordersStore.js